import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/i18n";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Plus, Pencil, Trash2, Star, ExternalLink, FileDown, PlayCircle, Search,
  Image as ImageIcon, Upload, MousePointerClick, BookOpen, Code2, Copy, BarChart3,
  FolderOpen, ChevronLeft, Share2, Paperclip, Heart, Music, LayoutGrid, List, HardDriveDownload,
} from "lucide-react";
import DriveImportDialog from "@/components/DriveImportDialog";
import { toast } from "sonner";
import { todayISO } from "@/lib/dateFormat";

const LEVELS = ["all", "A1", "A2", "B1", "B2", "C1"];
const EMBED_HOSTS = [
  "canva.com", "youtube.com", "youtube-nocookie.com", "youtu.be", "vimeo.com",
  "quizlet.com", "genial.ly", "genially.com", "wordwall.net", "padlet.com",
  "google.com", "docs.google.com", "drive.google.com", "forms.gle",
  "learningapps.org", "liveworksheets.com", "h5p.org", "wooclap.com",
  "soundcloud.com", "spotify.com", "anchor.fm", "kahoot.it", "wordart.com",
  "flipbookpdf.net", "heyzine.com", "issuu.com", "slideshare.net", "loom.com",
];

const embedSrcOf = (code) => {
  if (!code || !code.trim()) return { src: null, error: null };
  const m = code.match(/<iframe[^>]*\ssrc\s*=\s*["']([^"']+)["']/i);
  const src = m ? m[1] : (code.trim().startsWith("http") ? code.trim() : null);
  if (!src) return { src: null, error: "no_iframe" };
  let host = "";
  try { host = new URL(src).hostname.toLowerCase().replace(/^www\./, ""); } catch { return { src: null, error: "no_iframe" }; }
  const ok = EMBED_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
  if (!ok) return { src: null, error: host };
  return { src, error: null };
};
const KIND_ICON = { link: ExternalLink, pdf: FileDown, video: PlayCircle, embed: Code2, file: FileDown, image: ImageIcon, audio: Music };
const FILE_KINDS = ["pdf", "file", "image", "audio"];
const FILE_ACCEPT = [
  "application/pdf", ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx",
  ".txt", ".csv", ".zip", "image/png", "image/jpeg", "image/webp", "image/gif",
  "audio/mpeg", "audio/mp4", "audio/wav", "audio/ogg", ".m4a",
].join(",");

/** YouTube video id from a watch/short/embed URL → its cover image. */
const ytThumb = (...urls) => {
  for (const u of urls) {
    if (!u) continue;
    const m = String(u).match(/(?:youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([\w-]{11})/);
    if (m) return `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg`;
  }
  return null;
};
const PALETTE = [
  "from-sky-500 to-blue-700", "from-amber-400 to-orange-600", "from-emerald-400 to-teal-700",
  "from-fuchsia-500 to-purple-700", "from-rose-400 to-red-600", "from-indigo-400 to-indigo-700",
];
const hueOf = (s = "") => PALETTE[[...s].reduce((n, c) => n + c.charCodeAt(0), 0) % PALETTE.length];

/** Accepts "16:9" or a free decimal ratio like "1.42" and returns a number. */
const ratioOf = (aspect) => {
  if (!aspect) return 16 / 9;
  if (String(aspect).includes(":")) {
    const [w, h] = String(aspect).split(":").map(Number);
    return w && h ? w / h : 16 / 9;
  }
  const n = Number(aspect);
  return n > 0.2 && n < 5 ? n : 16 / 9;
};

const emptyForm = () => ({
  title: "", description: "", category: "", subcategory_id: "", level: "all", kind: "link",
  url: "", embed_code: "", aspect: "16:9", image_url: "", visibility: "all", published: true,
});

export default function Resources() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isAdmin = user?.role === "superadmin" || user?.role === "academic_director";
  const isStaff = isAdmin || user?.role === "teacher";
  const canEditRes = (r) => isAdmin || (user?.role === "teacher" && r.created_by === user?.user_id && r.published === false);
  const [params, setParams] = useSearchParams();
  const [highlightId, setHighlightId] = useState(null);
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [level, setLevel] = useState("all");
  const [favOnly, setFavOnly] = useState(false);
  const [vis, setVis] = useState("any");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const imgRef = useRef(null);
  const pdfRef = useRef(null);
  const [brokenImgs, setBrokenImgs] = useState({});
  const [viewer, setViewer] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [stats, setStats] = useState(null);
  const [catCards, setCatCards] = useState([]);
  const [openCat, setOpenCat] = useState(null);
  const [openSub, setOpenSub] = useState(null);
  const [catOpen, setCatOpen] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [catForm, setCatForm] = useState({ name: "", description: "", image_url: "", visibility: "all", parent_id: "" });
  const [dragCatId, setDragCatId] = useState(null);
  const [view, setView] = useState(() => localStorage.getItem("resources_view") || "cards");
  const [importOpen, setImportOpen] = useState(false);
  useEffect(() => { localStorage.setItem("resources_view", view); }, [view]);
  const catImgRef = useRef(null);
  const searching = q.trim() !== "" || favOnly;
  const showCategories = !openCat && !searching;
  const filtersActive = q.trim() !== "" || cat !== "all" || level !== "all" || favOnly;
  const canReorder = isAdmin && !filtersActive;
  const visMatch = (c) => vis === "any" || !isStaff || (c.visibility || "all") === vis;
  const topCards = catCards.filter((c) => !c.parent_id && visMatch(c));
  const curCat = catCards.find((c) => !c.parent_id && c.name === openCat) || null;
  const subCards = curCat ? catCards.filter((c) => c.parent_id === curCat.id && visMatch(c)) : [];
  const formCat = catCards.find((c) => !c.parent_id && c.name === form.category) || null;
  const formSubs = formCat ? catCards.filter((c) => c.parent_id === formCat.id) : [];
  const subName = (id) => catCards.find((c) => c.id === id)?.name || null;

  const leaveCat = () => { setOpenCat(null); setOpenSub(null); };

  const onDropCat = async (targetId) => {
    if (!isAdmin || !dragCatId || dragCatId === targetId) return setDragCatId(null);
    const dragged = catCards.find((c) => c.id === dragCatId);
    const target = catCards.find((c) => c.id === targetId);
    const sameLevel = dragged && target && (dragged.parent_id || null) === (target.parent_id || null);
    if (!sameLevel) return setDragCatId(null);
    const sibs = catCards.filter((c) => (c.parent_id || null) === (dragged.parent_id || null));
    const others = catCards.filter((c) => (c.parent_id || null) !== (dragged.parent_id || null));
    const from = sibs.findIndex((c) => c.id === dragCatId);
    const to = sibs.findIndex((c) => c.id === targetId);
    if (from < 0 || to < 0) return setDragCatId(null);
    const next = [...sibs];
    next.splice(to, 0, next.splice(from, 1)[0]);
    setCatCards([...next, ...others]);
    setDragCatId(null);
    try {
      await api.post("/resource-categories/reorder", { ids: next.map((c) => c.id) });
    } catch {
      toast.error(t("error_generic"));
      load();
    }
  };

  const submitCat = async () => {
    try {
      const body = { ...catForm };
      const res = editingCat
        ? await api.patch(`/resource-categories/${editingCat.id}`, body)
        : await api.post("/resource-categories", body);
      if (catImgRef.current?.files?.[0]) {
        const fd = new FormData();
        fd.append("file", catImgRef.current.files[0]);
        await api.post(`/resource-categories/${res.data.id}/image`, fd);
      }
      toast.success(t("saved"));
      setCatOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    }
  };

  const removeCat = async (c) => {
    const kids = catCards.filter((x) => x.parent_id === c.id).length;
    if (kids) {
      toast.error("Borrá primero sus subcategorías");
      return;
    }
    const msg = c.parent_id
      ? "¿Borrar la subcategoría? Sus recursos quedan en la categoría madre."
      : t("resource_category_delete_confirm");
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/resource-categories/${c.id}`);
      toast.success(t("deleted"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    }
  };

  const onDrop = async (targetId) => {
    if (!canReorder || !dragId || dragId === targetId) return setDragId(null);
    const from = rows.findIndex((r) => r.id === dragId);
    const to = rows.findIndex((r) => r.id === targetId);
    if (from < 0 || to < 0) return setDragId(null);
    const next = [...rows];
    next.splice(to, 0, next.splice(from, 1)[0]);
    setRows(next);
    setDragId(null);
    try {
      await api.post("/resources/reorder", { ids: next.map((r) => r.id) });
    } catch {
      toast.error(t("error_generic"));
      load();
    }
  };

  const load = async () => {
    const params = {};
    if (cat !== "all") params.category = cat;
    if (level !== "all") params.level = level;
    if (isStaff) params.include_unpublished = true;
    if (isStaff && vis !== "any") params.visibility = vis;
    const [r, c, cc] = await Promise.all([
      api.get("/resources", { params }),
      api.get("/resources/categories"),
      api.get("/resource-categories"),
    ]);
    setRows(r.data || []);
    setCats(c.data || []);
    setCatCards(cc.data || []);
  };

  useEffect(() => { load().catch(() => {}); /* eslint-disable-next-line */ }, [cat, level]);

  // Deep link: /dashboard/resources?r=<id> opens the resource's category and highlights it.
  useEffect(() => {
    const rid = params.get("r");
    if (!rid || rows.length === 0) return;
    const found = rows.find((x) => x.id === rid);
    if (!found) return;
    setOpenCat(found.category);
    setOpenSub(found.subcategory_id ? (catCards.find((c) => c.id === found.subcategory_id) || null) : null);
    setHighlightId(rid);
    setParams({}, { replace: true });
    if (found.kind === "embed" && found.embed_src) setViewer(found);
    setTimeout(() => {
      document.querySelector(`[data-testid="resource-card-${rid}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 300);
    setTimeout(() => setHighlightId(null), 4000);
    /* eslint-disable-next-line */
  }, [rows]);

  const [attachRes, setAttachRes] = useState(null);
  const [myGroups, setMyGroups] = useState([]);
  const [attachGroup, setAttachGroup] = useState("");
  const [groupHw, setGroupHw] = useState([]);
  const [attachHw, setAttachHw] = useState("");
  const [newHw, setNewHw] = useState({ title: "", date: todayISO() });

  const openAttach = async (r) => {
    setAttachRes(r);
    setAttachGroup("");
    setAttachHw("");
    setGroupHw([]);
    setNewHw({ title: "", date: todayISO() });
    const g = await api.get("/groups");
    setMyGroups(g.data || []);
  };

  const pickAttachGroup = async (gid) => {
    setAttachGroup(gid);
    setAttachHw("");
    const r = await api.get("/homework", { params: { group_id: gid } });
    setGroupHw(r.data || []);
  };

  const confirmAttach = async () => {
    try {
      let hwId = attachHw;
      if (hwId === "__new__") {
        if (!newHw.title.trim()) return toast.error(t("resource_attach_need_title"));
        const created = await api.post("/homework", {
          group_id: attachGroup, date: newHw.date, title: newHw.title.trim(), description: "",
        });
        hwId = created.data.id;
      }
      await api.post(`/homework/${hwId}/resources`, { resource_ids: [attachRes.id] });
      toast.success(t("resource_attached"));
      setAttachRes(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    }
  };

  const shareLink = async (r) => {    const url = `${window.location.origin}/dashboard/resources?r=${r.id}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    toast.success(t("resource_link_copied"));
  };

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (openCat && r.category !== openCat) return false;
      if (openSub) {
        if (r.subcategory_id !== openSub.id) return false;
      } else if (openCat && !needle && !favOnly && r.subcategory_id) {
        return false; // los recursos dentro de subcategorías se ven al abrirlas
      }
      if (favOnly && !r.is_favorite) return false;
      if (!needle) return true;
      return `${r.title} ${r.description || ""} ${r.category}`.toLowerCase().includes(needle);
    });
  }, [rows, q, favOnly, openCat, openSub]);

  const openResource = async (r) => {
    api.post(`/resources/${r.id}/click`).catch(() => {});
    setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, clicks: (x.clicks || 0) + 1 } : x)));
    if (r.kind === "embed" && r.embed_src) {
      setViewer(r);
      return;
    }
    if (FILE_KINDS.includes(r.kind) && r.has_file) {
      const res = await api.get(`/resources/${r.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (r.kind === "pdf" || r.kind === "image" || r.kind === "audio") {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = r.file_name || "recurso";
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      return;
    }
    if (r.url) window.open(r.url, "_blank", "noopener,noreferrer");
  };

  const toggleFav = async (r) => {
    const next = !r.is_favorite;
    setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, is_favorite: next } : x)));
    try {
      if (next) await api.post(`/resources/${r.id}/favorite`);
      else await api.delete(`/resources/${r.id}/favorite`);
    } catch {
      setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, is_favorite: !next } : x)));
    }
  };

  const toggleLike = async (r) => {
    const next = !r.i_liked;
    setRows((prev) => prev.map((x) => (x.id === r.id
      ? { ...x, i_liked: next, like_count: Math.max(0, (x.like_count || 0) + (next ? 1 : -1)) } : x)));
    try {
      if (next) await api.post(`/resources/${r.id}/like`);
      else await api.delete(`/resources/${r.id}/like`);
    } catch {
      setRows((prev) => prev.map((x) => (x.id === r.id
        ? { ...x, i_liked: !next, like_count: Math.max(0, (x.like_count || 0) + (next ? -1 : 1)) } : x)));
    }
  };

  const submit = async () => {
    try {
      const body = { ...form, category: form.category || "Otros", subcategory_id: form.subcategory_id || null };
      const res = editing
        ? await api.patch(`/resources/${editing.id}`, body)
        : await api.post("/resources", body);
      const id = res.data.id;
      if (imgRef.current?.files?.[0]) {
        const fd = new FormData();
        fd.append("file", imgRef.current.files[0]);
        await api.post(`/resources/${id}/image`, fd);
      }
      if (pdfRef.current?.files?.[0]) {
        const fd = new FormData();
        fd.append("file", pdfRef.current.files[0]);
        await api.post(`/resources/${id}/file`, fd);
      }
      toast.success(t("saved"));
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    }
  };

  const duplicate = async (r) => {
    try {
      const res = await api.post(`/resources/${r.id}/duplicate`);
      await load();
      startEdit(res.data);
      toast.success(t("resource_duplicated"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    }
  };

  const openRanking = async () => {
    const r = await api.get("/resources-stats");
    setStats(r.data);
  };

  const remove = async (r) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/resources/${r.id}`);
    toast.success(t("deleted"));
    load();
  };

  const startEdit = (r) => {
    setEditing(r);
    setForm({
      title: r.title, description: r.description || "", category: r.category, level: r.level,
      subcategory_id: r.subcategory_id || "",
      kind: r.kind, url: r.url || "", embed_code: r.embed_code || "",
      aspect: r.aspect || "16:9", visibility: r.visibility || "all",
      image_url: r.image_url || "", published: r.published !== false,
    });
    setOpen(true);
  };

  const cover = (r) => {
    if (brokenImgs[r.id]) return null;
    if (r.has_image) return `${process.env.REACT_APP_BACKEND_URL}/api/resources/${r.id}/image?v=${r.updated_at || ""}`;
    const own = r.kind === "embed"
      ? ytThumb(r.embed_src, r.embed_code, r.url)
      : ytThumb(r.url, r.embed_src, r.embed_code);
    // a YouTube thumb copied from another resource (duplicates) must not win over its own video
    if (own && /img\.youtube\.com/.test(r.image_url || "")) return own;
    if (!r.image_url && !own && r.kind === "image" && r.has_file) {
      return `${process.env.REACT_APP_BACKEND_URL}/api/resources/${r.id}/file`;
    }
    return r.image_url || own || null;
  };

  const catCover = (c) => {
    if (brokenImgs[c.id]) return null;
    if (c.has_image) return `${process.env.REACT_APP_BACKEND_URL}/api/resource-categories/${c.id}/image?v=${c.updated_at || ""}`;
    return c.image_url || null;
  };

  const renderCatCard = (c, onOpen) => {
    const img = catCover(c);
    return (
      <Card
        key={c.id}
        draggable={isAdmin}
        onDragStart={() => setDragCatId(c.id)}
        onDragOver={(e) => { if (isAdmin && dragCatId) e.preventDefault(); }}
        onDrop={() => onDropCat(c.id)}
        className={`group overflow-hidden border-slate-200 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 ${isAdmin ? "cursor-grab active:cursor-grabbing" : ""} ${dragCatId === c.id ? "opacity-40" : ""}`}
        data-testid={`category-card-${c.id}`}
      >
        <button
          type="button"
          onClick={onOpen}
          className="relative block h-40 w-full overflow-hidden text-left"
          data-testid={`category-open-${c.id}`}
        >
          {img ? (
            <img
              src={img}
              alt={c.name}
              onError={() => setBrokenImgs((b) => ({ ...b, [c.id]: true }))}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          ) : (
            <div className={`h-full w-full bg-gradient-to-br ${hueOf(c.name)} flex items-center justify-center`}>
              <FolderOpen className="h-10 w-10 text-white/90" />
            </div>
          )}
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-4 pt-8 pb-3">
            <div className="text-white font-bold text-lg leading-tight drop-shadow">{c.name}</div>
            <div className="text-white/85 text-xs font-medium" data-testid={`category-count-${c.id}`}>
              {c.count} {c.count === 1 ? t("resource_count_one") : t("resource_count_many")}
            </div>
          </div>
          {c.visibility === "staff" ? (
            <span className="absolute top-2 right-2 rounded-full bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5" data-testid={`category-staff-only-${c.id}`}>
              {t("resource_vis_staff")}
            </span>
          ) : isStaff && (
            <span className="absolute top-2 right-2 rounded-full bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5" data-testid={`category-public-${c.id}`}>
              {t("resource_vis_public_badge")}
            </span>
          )}
          {isAdmin && c.count === 0 && (
            <span className="absolute top-2 left-2 rounded-full bg-slate-800 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
              {t("resource_category_hidden")}
            </span>
          )}
        </button>
        <div className="p-4 flex items-center gap-2">
          {c.description && <p className="flex-1 text-xs text-slate-500 line-clamp-2">{c.description}</p>}
          <Button size="sm" className={c.description ? "" : "flex-1"} onClick={onOpen} data-testid={`category-cta-${c.id}`}>
            <FolderOpen className="h-3.5 w-3.5" /> {t("resource_open")}
          </Button>
          {isAdmin && (
            <>
              <Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`category-edit-${c.id}`}
                onClick={() => {
                  setEditingCat(c);
                  setCatForm({
                    name: c.name, description: c.description || "", image_url: c.image_url || "",
                    visibility: c.visibility || "all", parent_id: c.parent_id || "",
                  });
                  setCatOpen(true);
                }}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => removeCat(c)} data-testid={`category-delete-${c.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
        </div>
      </Card>
    );
  };

  const renderCatRow = (c, onOpen) => (
    <div
      key={c.id}
      draggable={isAdmin}
      onDragStart={() => setDragCatId(c.id)}
      onDragOver={(e) => { if (isAdmin && dragCatId) e.preventDefault(); }}
      onDrop={() => onDropCat(c.id)}
      className={`flex items-center gap-3 px-4 py-2.5 border-b border-slate-50 last:border-0 hover:bg-slate-50 ${isAdmin ? "cursor-grab active:cursor-grabbing" : ""} ${dragCatId === c.id ? "opacity-40" : ""}`}
      data-testid={`category-row-${c.id}`}
    >
      <FolderOpen className="h-4 w-4 text-slate-500 shrink-0" />
      <button type="button" onClick={onOpen} data-testid={`category-open-${c.id}`}
              className="flex-1 min-w-0 text-left text-sm font-medium text-slate-800 hover:text-brand truncate">
        {c.name}
      </button>
      {c.visibility === "staff" ? (
        <span className="rounded-full bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5" data-testid={`category-staff-only-${c.id}`}>
          {t("resource_vis_staff")}
        </span>
      ) : isStaff && (
        <span className="rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5" data-testid={`category-public-${c.id}`}>
          {t("resource_vis_public_badge")}
        </span>
      )}
      <span className="hidden sm:block w-40 text-xs text-slate-500 truncate">{c.description || "—"}</span>
      <span className="w-24 text-xs text-slate-500 text-right" data-testid={`category-count-${c.id}`}>
        {c.count} {c.count === 1 ? t("resource_count_one") : t("resource_count_many")}
      </span>
      <span className="w-24 flex items-center justify-end gap-0.5">
        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onOpen} data-testid={`category-cta-${c.id}`}>
          <FolderOpen className="h-3.5 w-3.5" />
        </Button>
        {isAdmin && (
          <>
            <Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`category-edit-${c.id}`}
              onClick={() => {
                setEditingCat(c);
                setCatForm({
                  name: c.name, description: c.description || "", image_url: c.image_url || "",
                  visibility: c.visibility || "all", parent_id: c.parent_id || "",
                });
                setCatOpen(true);
              }}>
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => removeCat(c)} data-testid={`category-delete-${c.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </>
        )}
      </span>
    </div>
  );

  return (
    <div>
      <PageHeader        title={openSub?.name || openCat || t("resources")}
        subtitle={openSub ? `${openCat} · subcategoría` : openCat ? t("resource_category") : t("resources_sub")}
        testId="resources-header"
        action={isStaff && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="drive-import-button">
              <HardDriveDownload className="h-4 w-4" /> Importar de Drive
            </Button>
            {isAdmin && (
              <Button variant="outline" onClick={openRanking} data-testid="resource-ranking-button">
                <BarChart3 className="h-4 w-4" /> {t("resource_ranking")}
              </Button>
            )}
            {showCategories && isAdmin ? (
              <Button onClick={() => { setEditingCat(null); setCatForm({ name: "", description: "", image_url: "", visibility: "all", parent_id: "" }); setCatOpen(true); }} data-testid="new-category-button">
                <Plus className="h-4 w-4" /> {t("resource_category_new")}
              </Button>
            ) : (
              <>
                {isAdmin && curCat && !openSub && (
                  <Button
                    variant="outline"
                    onClick={() => { setEditingCat(null); setCatForm({ name: "", description: "", image_url: "", visibility: "all", parent_id: curCat.id }); setCatOpen(true); }}
                    data-testid="new-subcategory-button"
                  >
                    <Plus className="h-4 w-4" /> Nueva subcategoría
                  </Button>
                )}
                <Button onClick={() => { setEditing(null); setForm({ ...emptyForm(), category: openCat || "", subcategory_id: openSub?.id || "" }); setOpen(true); }} data-testid="new-resource-button">
                  <Plus className="h-4 w-4" /> {t("resource_new")}
                </Button>
              </>
            )}
          </div>
        )}
      />

      {openCat && (
        <div className="flex items-center gap-1 mb-3 -mt-2">
          <Button variant="ghost" size="sm" onClick={leaveCat} data-testid="back-to-categories">
            <ChevronLeft className="h-4 w-4" /> {t("resource_all_categories")}
          </Button>
          {openSub && (
            <Button variant="ghost" size="sm" onClick={() => setOpenSub(null)} data-testid="back-to-category">
              <ChevronLeft className="h-4 w-4" /> {openCat}
            </Button>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            className="pl-9"
            placeholder={t("resource_search")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="resource-search-input"
          />
        </div>
        {!showCategories && (
          <>
            <div>
              <Label className="text-xs">{t("resource_category")}</Label>
              <Select value={cat} onValueChange={setCat}>
                <SelectTrigger className="w-[190px]" data-testid="resource-filter-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("all")}</SelectItem>
                  {cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t("level")}</Label>
              <Select value={level} onValueChange={setLevel}>
                <SelectTrigger className="w-[140px]" data-testid="resource-filter-level"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LEVELS.map((l) => <SelectItem key={l} value={l}>{l === "all" ? t("all") : l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </>
        )}
        {isStaff && (
          <div>
            <Label className="text-xs">{t("resource_visibility")}</Label>
            <Select value={vis} onValueChange={setVis}>
              <SelectTrigger className="w-[170px]" data-testid="resource-filter-visibility"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">{t("all")}</SelectItem>
                <SelectItem value="all">{t("resource_vis_all")}</SelectItem>
                <SelectItem value="staff">{t("resource_vis_staff")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        <Button
          variant={favOnly ? "default" : "outline"}
          onClick={() => setFavOnly((v) => !v)}
          data-testid="resource-favorites-toggle"
        >
          <Star className={`h-4 w-4 ${favOnly ? "fill-current" : ""}`} /> {t("resource_favorites")}
        </Button>
        <div className="ml-auto flex items-center rounded-lg border border-slate-200 overflow-hidden">
          <button type="button" onClick={() => setView("cards")} data-testid="resources-view-cards"
                  title={t("resource_view_cards")}
                  className={`h-9 w-9 grid place-items-center ${view === "cards" ? "bg-slate-100 text-slate-900" : "text-slate-400 hover:text-slate-600"}`}>
            <LayoutGrid className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => setView("list")} data-testid="resources-view-list"
                  title={t("resource_view_list")}
                  className={`h-9 w-9 grid place-items-center border-l border-slate-200 ${view === "list" ? "bg-slate-100 text-slate-900" : "text-slate-400 hover:text-slate-600"}`}>
            <List className="h-4 w-4" />
          </button>
        </div>
      </div>

      {isAdmin && (
        <p className="text-[11px] text-slate-500 -mt-3 mb-4" data-testid="resource-reorder-hint">
          {showCategories ? t("resource_reorder_cats_hint") : canReorder ? t("resource_reorder_hint") : t("resource_reorder_blocked")}
        </p>
      )}

      {/* Level 1: category cards */}
      {showCategories && (view === "list" ? (
        <Card className="overflow-hidden" data-testid="categories-list">
          <div className="hidden sm:flex items-center gap-3 px-4 py-2 border-b border-slate-100 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
            <span className="flex-1">{t("resource_category")}</span>
            <span className="w-40">{t("description")}</span>
            <span className="w-24 text-right">#</span>
            <span className="w-24 text-right">{t("actions")}</span>
          </div>
          {topCards.map((c) => renderCatRow(c, () => { setOpenCat(c.name); setOpenSub(null); }))}
          {topCards.length === 0 && (
            <div className="p-10 text-center text-slate-500" data-testid="categories-empty">
              <FolderOpen className="h-8 w-8 mx-auto mb-3 text-slate-300" />
              {t("resource_empty")}
            </div>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="categories-grid">
          {topCards.map((c) => renderCatCard(c, () => { setOpenCat(c.name); setOpenSub(null); }))}
          {topCards.length === 0 && (
            <Card className="p-10 text-center text-slate-500 col-span-full" data-testid="categories-empty">
              <FolderOpen className="h-8 w-8 mx-auto mb-3 text-slate-300" />
              {t("resource_empty")}
            </Card>
          )}
        </div>
      ))}

      {/* Level 2: subcategory cards inside a category */}
      {openCat && !openSub && !searching && subCards.length > 0 && (
        <div className="mb-8">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Subcategorías</div>
          {view === "list" ? (
            <Card className="overflow-hidden" data-testid="subcategories-list">
              {subCards.map((c) => renderCatRow(c, () => setOpenSub(c)))}
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="subcategories-grid">
              {subCards.map((c) => renderCatCard(c, () => setOpenSub(c)))}
            </div>
          )}
        </div>
      )}

      {!showCategories && visible.length === 0 && (
        <Card className="p-10 text-center text-slate-500" data-testid="resources-empty">
          <BookOpen className="h-8 w-8 mx-auto mb-3 text-slate-300" />
          {t("resource_empty")}
        </Card>
      )}

      {!showCategories && view === "list" && visible.length > 0 && (
        <Card className="overflow-hidden" data-testid="resources-list">
          <div className="hidden sm:flex items-center gap-3 px-4 py-2 border-b border-slate-100 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
            <span className="flex-1">{t("title")}</span>
            <span className="w-44">{t("resource_category")}</span>
            <span className="w-20 text-center">{t("level")}</span>
            <span className="w-44 text-right">{t("actions")}</span>
          </div>
          {visible.map((r) => {
            const Icon = KIND_ICON[r.kind] || ExternalLink;
            return (
              <div
                key={r.id}
                draggable={canReorder}
                onDragStart={() => setDragId(r.id)}
                onDragOver={(e) => { if (canReorder && dragId) e.preventDefault(); }}
                onDrop={() => onDrop(r.id)}
                className={`flex items-center gap-3 px-4 py-2.5 border-b border-slate-50 last:border-0 hover:bg-slate-50 ${canReorder ? "cursor-grab active:cursor-grabbing" : ""} ${dragId === r.id ? "opacity-40" : ""} ${highlightId === r.id ? "ring-2 ring-brand ring-inset" : ""}`}
                data-testid={`resource-row-${r.id}`}
              >
                <Icon className="h-4 w-4 text-slate-500 shrink-0" />
                <button type="button" onClick={() => openResource(r)} data-testid={`resource-open-${r.id}`}
                        className="flex-1 min-w-0 text-left text-sm text-slate-800 hover:text-brand truncate">
                  {r.title}
                </button>
                {r.published === false && (
                  <span className="rounded-full bg-slate-800 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 shrink-0">
                    {r.needs_review ? t("resource_pending_review") : t("resource_draft")}
                  </span>
                )}
                {r.visibility === "staff" && (
                  <span className="rounded-full bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 shrink-0" data-testid={`resource-staff-only-${r.id}`}>
                    {t("resource_vis_staff")}
                  </span>
                )}
                <span className="hidden sm:block w-44 text-xs text-slate-500 truncate">
                  {r.category}{r.subcategory_id && subName(r.subcategory_id) ? ` · ${subName(r.subcategory_id)}` : ""}
                </span>
                <span className="hidden sm:block w-20 text-center text-[11px] font-bold text-slate-600" data-testid={`resource-level-${r.id}`}>
                  {r.level === "all" ? t("all_levels") : r.level}
                </span>
                <span className="w-44 flex items-center justify-end gap-0.5">
                  <button type="button" onClick={() => toggleLike(r)}
                          className={`rounded-full px-1.5 py-1 flex items-center gap-1 text-[11px] font-semibold ${r.i_liked ? "text-rose-600" : "text-slate-400 hover:text-rose-600"}`}
                          data-testid={`resource-like-${r.id}`}>
                    <Heart className={`h-3.5 w-3.5 ${r.i_liked ? "fill-current" : ""}`} />
                    <span data-testid={`resource-like-count-${r.id}`}>{r.like_count || 0}</span>
                  </button>
                  <Button size="icon" variant="ghost" className={`h-8 w-8 ${r.is_favorite ? "text-amber-500" : "text-slate-300"}`}
                          onClick={() => toggleFav(r)} title={t("resource_favorites")} data-testid={`resource-fav-${r.id}`}>
                    <Star className={`h-3.5 w-3.5 ${r.is_favorite ? "fill-current" : ""}`} />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openResource(r)} data-testid={`resource-cta-${r.id}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </Button>
                  {isStaff && r.published !== false && (
                    <>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openAttach(r)} title={t("resource_attach")} data-testid={`resource-attach-${r.id}`}>
                        <Paperclip className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => shareLink(r)} title={t("resource_share")} data-testid={`resource-share-${r.id}`}>
                        <Share2 className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  )}
                  {canEditRes(r) && (
                    <>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => duplicate(r)} title={t("resource_duplicate")} data-testid={`resource-duplicate-${r.id}`}>
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => startEdit(r)} data-testid={`resource-edit-${r.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => remove(r)} data-testid={`resource-delete-${r.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  )}
                </span>
              </div>
            );
          })}
        </Card>
      )}

      {!showCategories && view === "cards" && (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="resources-grid">
        {visible.map((r) => {
          const Icon = KIND_ICON[r.kind] || ExternalLink;
          const img = cover(r);
          return (
            <Card
              key={r.id}
              draggable={canReorder}
              onDragStart={() => setDragId(r.id)}
              onDragOver={(e) => { if (canReorder && dragId) e.preventDefault(); }}
              onDrop={() => onDrop(r.id)}
              className={`group overflow-hidden border-slate-200 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 flex flex-col ${canReorder ? "cursor-grab active:cursor-grabbing" : ""} ${dragId === r.id ? "opacity-40" : ""} ${highlightId === r.id ? "ring-2 ring-brand ring-offset-2" : ""}`}
              data-testid={`resource-card-${r.id}`}
            >
              <button
                type="button"
                onClick={() => openResource(r)}
                className="relative block h-36 w-full overflow-hidden text-left"
                data-testid={`resource-open-${r.id}`}
              >
                {img ? (
                  <img
                    src={img}
                    alt={r.title}
                    onError={() => setBrokenImgs((b) => ({ ...b, [r.id]: true }))}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                ) : (
                  <div className={`h-full w-full bg-gradient-to-br ${hueOf(r.category)} flex items-center justify-center`}>
                    <Icon className="h-9 w-9 text-white/90" />
                  </div>
                )}
                <div className="absolute top-2 left-2 flex gap-1.5">
                  {r.is_new && (
                    <span className="rounded-full bg-brand text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5" data-testid={`resource-new-${r.id}`}>
                      {t("resource_new_badge")}
                    </span>
                  )}
                  {r.published === false && (
                    <span className="rounded-full bg-slate-800 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
                      {r.needs_review ? t("resource_pending_review") : t("resource_draft")}
                    </span>
                  )}
                  {r.visibility === "staff" && (
                    <span className="rounded-full bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5" data-testid={`resource-staff-only-${r.id}`}>
                      {t("resource_vis_staff")}
                    </span>
                  )}
                </div>
                <span className="absolute bottom-2 right-2 rounded-lg bg-white/95 backdrop-blur px-2.5 py-1 text-sm font-bold tracking-wide text-slate-900 shadow-sm" data-testid={`resource-level-${r.id}`}>
                  {r.level === "all" ? t("all_levels") : r.level}
                </span>
              </button>

              <div className="p-4 flex-1 flex flex-col gap-2">
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-brand-blue font-semibold">
                      {r.category}{r.subcategory_id && subName(r.subcategory_id) ? ` · ${subName(r.subcategory_id)}` : ""}
                    </div>
                    <div className="font-semibold text-slate-900 leading-tight truncate">{r.title}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleLike(r)}
                    className={`shrink-0 rounded-full px-2 py-1.5 flex items-center gap-1 text-[11px] font-semibold transition-colors ${r.i_liked ? "text-rose-600 bg-rose-50" : "text-slate-400 hover:text-rose-600 hover:bg-rose-50"}`}
                    title={t("like")}
                    data-testid={`resource-like-${r.id}`}
                  >
                    <Heart className={`h-4 w-4 ${r.i_liked ? "fill-current" : ""}`} />
                    <span data-testid={`resource-like-count-${r.id}`}>{r.like_count || 0}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleFav(r)}
                    className={`shrink-0 rounded-full p-1.5 transition-colors ${r.is_favorite ? "text-amber-500 bg-amber-50" : "text-slate-300 hover:text-amber-500 hover:bg-amber-50"}`}
                    title={t("resource_favorites")}
                    data-testid={`resource-fav-${r.id}`}
                  >
                    <Star className={`h-4 w-4 ${r.is_favorite ? "fill-current" : ""}`} />
                  </button>
                </div>
                {r.description && <p className="text-xs text-slate-500 line-clamp-2">{r.description}</p>}

                <div className="mt-auto flex items-center gap-2 pt-2">
                  <Button size="sm" className="flex-1" onClick={() => openResource(r)} data-testid={`resource-cta-${r.id}`}>
                    <Icon className="h-3.5 w-3.5" />
                    {FILE_KINDS.includes(r.kind) ? t("download") : r.kind === "video" ? t("resource_watch") : t("resource_open")}                  </Button>
                  {isStaff && r.published !== false && (
                    <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => openAttach(r)} title={t("resource_attach")} data-testid={`resource-attach-${r.id}`}>
                      <Paperclip className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {isStaff && r.published !== false && (
                    <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => shareLink(r)} title={t("resource_share")} data-testid={`resource-share-${r.id}`}>
                      <Share2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {canEditRes(r) && (
                    <>
                      <span className="flex items-center gap-1 text-[11px] text-slate-500" title={t("resource_clicks")} data-testid={`resource-clicks-${r.id}`}>
                        <MousePointerClick className="h-3.5 w-3.5" />{r.clicks || 0}
                      </span>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => duplicate(r)} title={t("resource_duplicate")} data-testid={`resource-duplicate-${r.id}`}>
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => startEdit(r)} data-testid={`resource-edit-${r.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => remove(r)} data-testid={`resource-delete-${r.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
      )}

      {/* Attach to homework */}
      <Dialog open={!!attachRes} onOpenChange={(o) => !o && setAttachRes(null)}>
        <DialogContent className="max-w-md" data-testid="attach-homework-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{t("resource_attach")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">{attachRes?.title}</p>
            <div>
              <Label>{t("group")}</Label>
              <Select value={attachGroup} onValueChange={pickAttachGroup}>
                <SelectTrigger data-testid="attach-group-select"><SelectValue placeholder={t("select")} /></SelectTrigger>
                <SelectContent>
                  {myGroups.map((g) => (
                    <SelectItem key={g.id} value={g.id}>{g.name}{g.is_private ? " · privada" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {attachGroup && (
              <div>
                <Label>{t("homework")}</Label>
                <Select value={attachHw} onValueChange={setAttachHw}>
                  <SelectTrigger data-testid="attach-homework-select"><SelectValue placeholder={t("select")} /></SelectTrigger>
                  <SelectContent>
                    {groupHw.map((h) => (
                      <SelectItem key={h.id} value={h.id}>{h.date} · {h.title}</SelectItem>
                    ))}
                    <SelectItem value="__new__">+ {t("resource_attach_new_hw")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {attachHw === "__new__" && (
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <Label>{t("title")}</Label>
                  <Input value={newHw.title} onChange={(e) => setNewHw({ ...newHw, title: e.target.value })} data-testid="attach-new-hw-title" />
                </div>
                <div>
                  <Label>{t("date")}</Label>
                  <Input type="date" value={newHw.date} onChange={(e) => setNewHw({ ...newHw, date: e.target.value })} data-testid="attach-new-hw-date" />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAttachRes(null)}>{t("cancel")}</Button>
            <Button onClick={confirmAttach} disabled={!attachGroup || !attachHw} data-testid="confirm-attach-homework">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category dialog */}
      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent className="max-w-md" data-testid="category-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>
              {catForm.parent_id
                ? (editingCat ? "Editar subcategoría" : "Nueva subcategoría")
                : (editingCat ? t("resource_category_edit") : t("resource_category_new"))}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t("resource_category_name")}</Label>
              <Input value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} data-testid="category-name-input" />
            </div>
            <div>
              <Label>{t("description")}</Label>
              <Textarea rows={2} value={catForm.description} onChange={(e) => setCatForm({ ...catForm, description: e.target.value })} data-testid="category-description-input" />
            </div>
            <div>
              <Label className="flex items-center gap-1"><ImageIcon className="h-3.5 w-3.5" /> {t("resource_cover")}</Label>
              <Input
                placeholder={t("resource_image_url_hint")}
                value={catForm.image_url}
                onChange={(e) => setCatForm({ ...catForm, image_url: e.target.value })}
                data-testid="category-image-url-input"
              />
              <Input type="file" accept="image/png,image/jpeg,image/webp" ref={catImgRef} className="mt-2" data-testid="category-image-input" />
              {editingCat?.has_image && (
                <div className="mt-2 flex items-center gap-2" data-testid="category-current-cover">
                  <img src={catCover(editingCat)} alt="" className="h-12 w-20 rounded object-cover border border-slate-200" />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      await api.delete(`/resource-categories/${editingCat.id}/image`);
                      setEditingCat({ ...editingCat, has_image: false });
                      toast.success(t("deleted"));
                      load();
                    }}
                    data-testid="category-remove-cover"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Quitar imagen subida
                  </Button>
                </div>
              )}
            </div>
            <div>
              <Label>{t("resource_visibility")}</Label>
              <Select value={catForm.visibility} onValueChange={(v) => setCatForm({ ...catForm, visibility: v })}>
                <SelectTrigger data-testid="category-visibility-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("resource_vis_all")}</SelectItem>
                  <SelectItem value="staff">{t("resource_vis_staff")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatOpen(false)}>{t("cancel")}</Button>
            <Button onClick={submitCat} data-testid="save-category-button">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Usage ranking */}
      <Dialog open={!!stats} onOpenChange={(o) => !o && setStats(null)}>
        <DialogContent className="max-w-xl" data-testid="resource-ranking-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{t("resource_ranking")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 mb-2">
            <Card className="p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">{t("resource_ranking_total")}</div>
              <div className="text-2xl font-bold" data-testid="ranking-total-clicks">{stats?.total_clicks ?? 0}</div>
            </Card>
            <Card className="p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">{t("resources")}</div>
              <div className="text-2xl font-bold">{stats?.total ?? 0}</div>
            </Card>
          </div>
          <div className="divide-y divide-slate-100" data-testid="ranking-list">
            {(stats?.top || []).map((r, i) => (
              <div key={r.id} className="flex items-center gap-3 py-2">
                <span className={`h-6 w-6 shrink-0 rounded-full text-[11px] font-bold flex items-center justify-center ${i < 3 ? "bg-brand text-white" : "bg-slate-100 text-slate-600"}`}>{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{r.title}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">{r.category}</div>
                </div>
                <span className="flex items-center gap-1 text-xs text-slate-600" title={t("resource_clicks")}>
                  <MousePointerClick className="h-3.5 w-3.5" />{r.clicks || 0}
                </span>
                <span className="flex items-center gap-1 text-xs text-amber-500" title={t("resource_favorites")}>
                  <Star className="h-3.5 w-3.5 fill-current" />{r.favorites || 0}
                </span>
                <span className="flex items-center gap-1 text-xs text-rose-600" title={t("like")} data-testid={`ranking-likes-${r.id}`}>
                  <Heart className="h-3.5 w-3.5 fill-current" />{r.likes || 0}
                </span>
              </div>
            ))}
            {(stats?.top || []).length === 0 && <p className="py-6 text-center text-sm text-slate-500">{t("resource_empty")}</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Embedded viewer */}
      <Dialog open={!!viewer} onOpenChange={(o) => !o && setViewer(null)}>
        <DialogContent className="max-w-6xl p-0 overflow-hidden" data-testid="resource-viewer" aria-describedby={undefined}>
          <DialogHeader className="px-5 pt-5 pb-3">
            <DialogTitle className="pr-8">{viewer?.title}</DialogTitle>
          </DialogHeader>
          <div className="bg-white flex justify-center">
            {viewer?.embed_src && (
              <iframe
                src={viewer.embed_src}
                title={viewer.title}
                className="w-full max-h-[76vh] border-0 bg-white"
                style={{ aspectRatio: ratioOf(viewer.aspect) }}
                allow="fullscreen; autoplay; encrypted-media"
                allowFullScreen
                loading="lazy"
                data-testid="resource-viewer-frame"
              />
            )}
          </div>
          <div className="flex justify-end items-center gap-2 px-5 py-3">
            {isAdmin && viewer && (
              <div className="mr-auto flex items-center gap-3" data-testid="viewer-aspect-controls">
                <input
                  type="range"
                  min="0.6"
                  max="2.4"
                  step="0.02"
                  value={ratioOf(viewer.aspect)}
                  onChange={(e) => {
                    const v = e.target.value;
                    setViewer((prev) => ({ ...prev, aspect: v }));
                  }}
                  onMouseUp={async (e) => {
                    const v = e.target.value;
                    setRows((prev) => prev.map((x) => (x.id === viewer.id ? { ...x, aspect: v } : x)));
                    await api.patch(`/resources/${viewer.id}`, { aspect: v });
                    toast.success(t("saved"));
                  }}
                  className="w-40 accent-brand cursor-pointer"
                  title={t("resource_aspect_fine")}
                  data-testid="viewer-aspect-slider"
                />
                <span className="text-[11px] text-slate-500 w-24">
                  {t("resource_aspect_fine")}: {ratioOf(viewer.aspect).toFixed(2)}
                </span>
              </div>
            )}
            <Button variant="outline" size="sm" onClick={() => window.open(viewer.embed_src, "_blank", "noopener,noreferrer")}>
              <ExternalLink className="h-3.5 w-3.5" /> {t("resource_open")}
            </Button>
            <Button size="sm" onClick={() => setViewer(null)}>{t("close")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="resource-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{editing ? t("resource_edit") : t("resource_new")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t("title")}</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="resource-title-input" />
            </div>
            <div>
              <Label>{t("description")}</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="resource-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("resource_category")}</Label>
                <Input
                  list="resource-cats"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  data-testid="resource-category-input"
                />
                <datalist id="resource-cats">
                  {cats.map((c) => <option key={c} value={c} />)}
                </datalist>
                <p className="text-[11px] text-slate-500 mt-1">Cambiá la categoría para mover el recurso.</p>
              </div>
              {formSubs.length > 0 && (
                <div className="col-span-2">
                  <Label>Subcategoría</Label>
                  <Select
                    value={form.subcategory_id || "none"}
                    onValueChange={(v) => setForm({ ...form, subcategory_id: v === "none" ? "" : v })}
                  >
                    <SelectTrigger data-testid="resource-subcategory-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin subcategoría</SelectItem>
                      {formSubs.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>{t("level")}</Label>
                <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                  <SelectTrigger data-testid="resource-level-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LEVELS.map((l) => <SelectItem key={l} value={l}>{l === "all" ? t("all_levels") : l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{t("resource_kind")}</Label>
              <Select value={FILE_KINDS.includes(form.kind) ? "file" : form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="resource-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="link">{t("resource_kind_link")}</SelectItem>
                  <SelectItem value="video">{t("resource_kind_video")}</SelectItem>
                  <SelectItem value="file">{t("resource_kind_file")}</SelectItem>
                  <SelectItem value="embed">{t("resource_kind_embed")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.kind === "embed" && (
              <div>
                <Label className="flex items-center gap-1"><Code2 className="h-3.5 w-3.5" /> {t("resource_embed_code")}</Label>
                <Textarea
                  rows={5}
                  className="font-mono text-[11px]"
                  placeholder='<iframe src="https://www.canva.com/design/.../view?embed" ...></iframe>'
                  value={form.embed_code}
                  onChange={(e) => setForm({ ...form, embed_code: e.target.value })}
                  data-testid="resource-embed-input"
                />
                <p className="text-[11px] text-slate-500 mt-1">{t("resource_embed_hint")}</p>
                <div className="mt-2">
                  <Label>{t("resource_aspect")}</Label>
                  <Select value={form.aspect} onValueChange={(v) => setForm({ ...form, aspect: v })}>
                    <SelectTrigger data-testid="resource-aspect-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="16:9">16:9 — {t("resource_aspect_wide")}</SelectItem>
                      <SelectItem value="4:3">4:3 — {t("resource_aspect_classic")}</SelectItem>
                      <SelectItem value="1:1">1:1 — {t("resource_aspect_square")}</SelectItem>
                      <SelectItem value="3:4">3:4 — {t("resource_aspect_portrait")}</SelectItem>
                      <SelectItem value="9:16">9:16 — {t("resource_aspect_story")}</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-500 mt-1">{t("resource_aspect_hint")}</p>
                </div>
                {(() => {
                  const { src, error } = embedSrcOf(form.embed_code);
                  if (src) {
                    return (
                      <div className="mt-2" data-testid="resource-embed-preview">
                        <div className="text-[10px] uppercase tracking-wider text-emerald-600 font-semibold mb-1">
                          {t("resource_embed_preview")}
                        </div>
                        <iframe
                          src={src}
                          title="preview"
                          className="w-full rounded-lg border border-slate-200 bg-white"
                          style={{ aspectRatio: ratioOf(form.aspect) }}
                          allow="fullscreen"
                          loading="lazy"
                        />
                      </div>
                    );
                  }
                  if (error === "no_iframe") {
                    return <p className="mt-2 text-[11px] text-red-600" data-testid="resource-embed-error">{t("resource_embed_no_iframe")}</p>;
                  }
                  if (error) {
                    return <p className="mt-2 text-[11px] text-red-600" data-testid="resource-embed-error">{t("resource_embed_bad_host")} {error}</p>;
                  }
                  return null;
                })()}
              </div>
            )}
            {!FILE_KINDS.includes(form.kind) && form.kind !== "embed" && (
              <div>
                <Label>{t("resource_url")}</Label>
                <Input placeholder="https://..." value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="resource-url-input" />
              </div>
            )}
            {FILE_KINDS.includes(form.kind) && (
              <div>
                <Label className="flex items-center gap-1"><Upload className="h-3.5 w-3.5" /> {t("resource_pdf")}</Label>
                <Input type="file" accept={FILE_ACCEPT} ref={pdfRef} data-testid="resource-pdf-input" />
                <p className="text-[11px] text-slate-500 mt-1">{t("resource_file_hint")}</p>
                {editing?.has_file && <p className="text-[11px] text-slate-600 mt-1 font-medium">{editing.file_name}</p>}
              </div>
            )}
            <div className="pt-2 border-t border-slate-100">
              <Label className="flex items-center gap-1"><ImageIcon className="h-3.5 w-3.5" /> {t("resource_cover")}</Label>
              <Input
                placeholder={t("resource_image_url_hint")}
                value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                data-testid="resource-image-url-input"
              />
              <Input type="file" accept="image/png,image/jpeg,image/webp" ref={imgRef} className="mt-2" data-testid="resource-image-input" />
              {editing?.has_image && (
                <div className="mt-2 flex items-center gap-2" data-testid="resource-current-cover">
                  <img src={cover(editing)} alt="" className="h-12 w-20 rounded object-cover border border-slate-200" />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      await api.delete(`/resources/${editing.id}/image`);
                      setEditing({ ...editing, has_image: false });
                      toast.success(t("deleted"));
                      load();
                    }}
                    data-testid="resource-remove-cover"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Quitar imagen subida
                  </Button>
                </div>
              )}
              {editing?.has_image && (
                <p className="text-[11px] text-slate-500 mt-1">
                  La imagen subida tiene prioridad. Si pegás un enlace y guardás, reemplaza la imagen subida.
                </p>
              )}
              {!form.image_url && ytThumb(form.kind === "embed" ? form.embed_code : form.url, form.embed_code) && (
                <div className="mt-2 flex items-center gap-2" data-testid="yt-thumb-preview">
                  <img src={ytThumb(form.kind === "embed" ? form.embed_code : form.url, form.embed_code)} alt="" className="h-12 w-20 rounded object-cover border border-slate-200" />
                  <p className="text-[11px] text-slate-500">Si lo dejás vacío, usamos la portada del video de YouTube.</p>
                </div>
              )}
            </div>
            <div className="pt-2 border-t border-slate-100">
              <Label>{t("resource_visibility")}</Label>
              <Select value={form.visibility} onValueChange={(v) => setForm({ ...form, visibility: v })}>
                <SelectTrigger data-testid="resource-visibility-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("resource_vis_all")}</SelectItem>
                  <SelectItem value="staff">{t("resource_vis_staff")}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">{t("resource_vis_hint")}</p>
            </div>
            {!isAdmin && (
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2" data-testid="teacher-review-note">
                {t("resource_teacher_note")}
              </p>
            )}
            <div className={`flex items-center justify-between gap-3 pt-2 border-t border-slate-100 ${isAdmin ? "" : "hidden"}`}>
              <div>
                <Label htmlFor="resource-published">{t("resource_published")}</Label>
                <p className="text-[11px] text-slate-500">{t("resource_published_hint")}</p>
              </div>
              <Switch
                id="resource-published"
                checked={form.published}
                onCheckedChange={(v) => setForm({ ...form, published: v })}
                data-testid="resource-published-switch"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={submit} data-testid="save-resource-button">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DriveImportDialog open={importOpen} onOpenChange={setImportOpen} isAdmin={isAdmin}
                         onImported={() => load().catch(() => {})} />
    </div>
  );
}
